import * as React from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import APIConnector from "../../../apiconnector";
import CommonVariables from "../../../variables";
import {
    emptyContact,
    shippingMethodOptions,
    phoneRegex,
    emailRegex,
    mapContactResponse,
    buildContactInputModel
} from "./contactHelper";

const usStateOptions = [
    { code: "AL", name: "Alabama", text: "AL - Alabama" },
    { code: "AK", name: "Alaska", text: "AK - Alaska" },
    { code: "AZ", name: "Arizona", text: "AZ - Arizona" },
    { code: "AR", name: "Arkansas", text: "AR - Arkansas" },
    { code: "CA", name: "California", text: "CA - California" },
    { code: "CO", name: "Colorado", text: "CO - Colorado" },
    { code: "CT", name: "Connecticut", text: "CT - Connecticut" },
    { code: "DE", name: "Delaware", text: "DE - Delaware" },
    { code: "FL", name: "Florida", text: "FL - Florida" },
    { code: "GA", name: "Georgia", text: "GA - Georgia" },
    { code: "HI", name: "Hawaii", text: "HI - Hawaii" },
    { code: "ID", name: "Idaho", text: "ID - Idaho" },
    { code: "IL", name: "Illinois", text: "IL - Illinois" },
    { code: "IN", name: "Indiana", text: "IN - Indiana" },
    { code: "IA", name: "Iowa", text: "IA - Iowa" },
    { code: "KS", name: "Kansas", text: "KS - Kansas" },
    { code: "KY", name: "Kentucky", text: "KY - Kentucky" },
    { code: "LA", name: "Louisiana", text: "LA - Louisiana" },
    { code: "ME", name: "Maine", text: "ME - Maine" },
    { code: "MD", name: "Maryland", text: "MD - Maryland" },
    { code: "MA", name: "Massachusetts", text: "MA - Massachusetts" },
    { code: "MI", name: "Michigan", text: "MI - Michigan" },
    { code: "MN", name: "Minnesota", text: "MN - Minnesota" },
    { code: "MS", name: "Mississippi", text: "MS - Mississippi" },
    { code: "MO", name: "Missouri", text: "MO - Missouri" },
    { code: "MT", name: "Montana", text: "MT - Montana" },
    { code: "NE", name: "Nebraska", text: "NE - Nebraska" },
    { code: "NV", name: "Nevada", text: "NV - Nevada" },
    { code: "NH", name: "New Hampshire", text: "NH - New Hampshire" },
    { code: "NJ", name: "New Jersey", text: "NJ - New Jersey" },
    { code: "NM", name: "New Mexico", text: "NM - New Mexico" },
    { code: "NY", name: "New York", text: "NY - New York" },
    { code: "NC", name: "North Carolina", text: "NC - North Carolina" },
    { code: "ND", name: "North Dakota", text: "ND - North Dakota" },
    { code: "OH", name: "Ohio", text: "OH - Ohio" },
    { code: "OK", name: "Oklahoma", text: "OK - Oklahoma" },
    { code: "OR", name: "Oregon", text: "OR - Oregon" },
    { code: "PA", name: "Pennsylvania", text: "PA - Pennsylvania" },
    { code: "RI", name: "Rhode Island", text: "RI - Rhode Island" },
    { code: "SC", name: "South Carolina", text: "SC - South Carolina" },
    { code: "SD", name: "South Dakota", text: "SD - South Dakota" },
    { code: "TN", name: "Tennessee", text: "TN - Tennessee" },
    { code: "TX", name: "Texas", text: "TX - Texas" },
    { code: "UT", name: "Utah", text: "UT - Utah" },
    { code: "VT", name: "Vermont", text: "VT - Vermont" },
    { code: "VA", name: "Virginia", text: "VA - Virginia" },
    { code: "WA", name: "Washington", text: "WA - Washington" },
    { code: "WV", name: "West Virginia", text: "WV - West Virginia" },
    { code: "WI", name: "Wisconsin", text: "WI - Wisconsin" },
    { code: "WY", name: "Wyoming", text: "WY - Wyoming" }
];

const normalizeCompareValue = (value) => {
    return value ? value.toString().trim().toLowerCase() : "";
};

const getSelectedStateOption = (stateValue) => {
    const normalizedStateValue = normalizeCompareValue(stateValue);

    if (!normalizedStateValue) {
        return null;
    }

    return usStateOptions.find((stateOption) => (
        normalizeCompareValue(stateOption.code) === normalizedStateValue
        || normalizeCompareValue(stateOption.name) === normalizedStateValue
        || normalizeCompareValue(stateOption.text) === normalizedStateValue
    )) || null;
};

const ContactModal = ({
    isOpen,
    isEditMode,
    initialContact,
    editingContactItem,
    parentCustomerId,
    onClose,
    onSaveSuccess,
    onLoaderRaise
}) => {
    const [newContact, setNewContact] = React.useState({
        ...emptyContact,
        ...(initialContact || {})
    });
    const [validationErrors, setValidationErrors] = React.useState({});

    React.useEffect(() => {
        const mappedContact = initialContact
            ? mapContactResponse([initialContact])[0] || initialContact
            : {};

        setNewContact({
            ...emptyContact,
            ...mappedContact,
            parentCustomerId: parentCustomerId || mappedContact.parentCustomerId || mappedContact.parentcustomerid || mappedContact.account_accountid || ""
        });
        setValidationErrors({});
    }, [initialContact, parentCustomerId, isOpen]);

    if (!isOpen) {
        return null;
    }

    const requiredValidator = (value, message) => {
        return value && value.toString().trim() ? "" : message;
    };

    const renderRequiredAsterisk = () => (
        <span style={{ color: "#d51923" }}>*</span>
    );

    const renderValidationError = (fieldName) => {
        if (!validationErrors[fieldName]) {
            return null;
        }

        return (
            <div style={{ color: "#d51923", fontSize: "12px", fontWeight: "bold", marginTop: "4px" }}>
                {validationErrors[fieldName]}
            </div>
        );
    };

    const handleNewContactChange = (fieldName, value) => {
        setNewContact((previousContact) => ({
            ...previousContact,
            [fieldName]: value
        }));

        setValidationErrors((previousErrors) => ({
            ...previousErrors,
            [fieldName]: ""
        }));
    };

    const handleTelephoneChange = (value) => {
        setNewContact((previousContact) => ({
            ...previousContact,
            telephoneNumber: value,
            telephone1: value,
            mobilephone: value
        }));

        setValidationErrors((previousErrors) => ({
            ...previousErrors,
            telephone1: ""
        }));
    };

    const telephoneValidator = () => {
        const telephone = (newContact.telephoneNumber || newContact.telephone1 || "").toString().trim();

        if (!telephone) {
            return "";
        }

        if (telephone.length > 18) {
            return "Telephone cannot exceed 18 characters.";
        }

        if (!phoneRegex.test(telephone)) {
            return "Invalid US phone number.";
        }

        return "";
    };

    const validateContactForm = () => {
        const email = newContact.emailaddress1 ? newContact.emailaddress1.toString().trim() : "";

        const errors = {
            firstName: requiredValidator(newContact.firstName, "First Name is required."),
            lastName: requiredValidator(newContact.lastName, "Last Name is required."),
            emailaddress1: !email
                ? "Email is required."
                : !emailRegex.test(email)
                    ? "Invalid email address."
                    : "",
            telephone1: telephoneValidator()
        };

        setValidationErrors(errors);

        return !Object.values(errors).some((errorMessage) => errorMessage);
    };

    const saveContact = () => {
        if (!validateContactForm()) {
            return;
        }

        const contactInputModel = buildContactInputModel({
            contact: newContact,
            editingContactItem,
            parentCustomerId
        });

        const saveContactApiUrl = isEditMode
            ? CommonVariables.urls.UpdateContact
            : CommonVariables.urls.CreateContact;

        if (onLoaderRaise) {
            onLoaderRaise(true, isEditMode ? "Updating contact ..." : "Creating contact ...", { marginTop: "145px" });
        }

        APIConnector.Post(saveContactApiUrl, contactInputModel).then(function (response) {
            if (onLoaderRaise) {
                onLoaderRaise(false);
            }

            if (!CommonVariables.isError(response)) {
                return;
            }

            if (onSaveSuccess) {
                onSaveSuccess(response, isEditMode);
            }
        });
    };

    return (
        <Dialog title={isEditMode ? "Edit Contact" : "Add Contact"} onClose={onClose} width={760}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px", padding: "8px 4px" }}>
                <div>
                    <label style={{ display: "block", marginBottom: "4px" }}>First Name {renderRequiredAsterisk()}</label>
                    <Input
                        required={true}
                        value={newContact.firstName}
                        onChange={(e) => handleNewContactChange("firstName", e.value)}
                    />
                    {renderValidationError("firstName")}
                </div>
                <div>
                    <label style={{ display: "block", marginBottom: "4px" }}>Last Name {renderRequiredAsterisk()}</label>
                    <Input
                        required={true}
                        value={newContact.lastName}
                        onChange={(e) => handleNewContactChange("lastName", e.value)}
                    />
                    {renderValidationError("lastName")}
                </div>
                <div>
                    <label style={{ display: "block", marginBottom: "4px" }}>Telephone</label>
                    <Input
                        value={newContact.telephoneNumber || newContact.telephone1 || ""}
                        onChange={(e) => handleTelephoneChange(e.value)}
                    />
                    {renderValidationError("telephone1")}
                </div>
                <div>
                    <label style={{ display: "block", marginBottom: "4px" }}>Email {renderRequiredAsterisk()}</label>
                    <Input
                        required={true}
                        value={newContact.emailaddress1}
                        onChange={(e) => handleNewContactChange("emailaddress1", e.value)}
                    />
                    {renderValidationError("emailaddress1")}
                </div>
                {isEditMode && 
                <div>
                    <label style={{ display: "block", marginBottom: "4px" }}>Shipping Method</label>
                    <DropDownList
                        data={shippingMethodOptions}
                        value={newContact.shipping_method}
                        defaultItem="Select Shipping Method"
                        onChange={(e) => handleNewContactChange("shipping_method", e.value)}
                    />
                </div>}

                <div style={{ gridColumn: "1 / 3", borderTop: "1px solid #ddd", paddingTop: "10px", marginTop: "4px" }}>
                    <h4 style={{ margin: "0 0 8px 0" }}>Address</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px" }}>
                        <div>
                            <label style={{ display: "block", marginBottom: "4px" }}>Street 1</label>
                            <Input
                                value={newContact.address1_line1}
                                onChange={(e) => handleNewContactChange("address1_line1", e.value)}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: "4px" }}>Street 2</label>
                            <Input
                                value={newContact.address1_line2}
                                onChange={(e) => handleNewContactChange("address1_line2", e.value)}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: "4px" }}>City</label>
                            <Input
                                value={newContact.address1_city}
                                onChange={(e) => handleNewContactChange("address1_city", e.value)}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: "4px" }}>State/Province</label>
                            <DropDownList
                                data={usStateOptions}
                                textField="text"
                                dataItemKey="code"
                                value={getSelectedStateOption(newContact.tt_stateprovinceid)}
                                defaultItem={{ code: "", name: "", text: "Select State/Province" }}
                                onChange={(e) => {
                                    handleNewContactChange("tt_stateprovinceid", e.value?.code || "");
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: "4px" }}>Postal Code</label>
                            <Input
                                value={newContact.address1_postalcode}
                                onChange={(e) => handleNewContactChange("address1_postalcode", e.value)}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: "4px" }}>County</label>
                            <Input
                                value={newContact.address1_county}
                                onChange={(e) => handleNewContactChange("address1_county", e.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ width: "20%", display: "flex", gap: "4px" }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button themeColor="primary" onClick={saveContact}>{isEditMode ? "Update" : "Save"}</Button>
            </div>
        </Dialog>
    );
};

export default ContactModal;
